import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const COMPANY = {
  name: "ZEMKA Gebäudereinigung & Hausmeister Service",
  owner: "Zemrije Ademi",
  street: "Sigmundstraße",
  city: "82377 Penzberg",
  country: "Deutschland",
  phone: "+49 170 8251779",
  email: "zemka.gebaeudereinigung@gmail.com",
};

export default function Impressum() {
  const { lang } = useLanguage();
  const isDe = lang === "de";

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="impressum-page">
      <header className="bg-[#0F172A] text-white py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-semibold tracking-wider uppercase mb-6 transition-colors"
            data-testid="impressum-back-link"
          >
            <ArrowLeft size={16} />
            {isDe ? "Zurück zur Startseite" : "Back to home"}
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            {isDe ? "Impressum" : "Imprint"}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl">
            {isDe
              ? "Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz)."
              : "Information according to § 5 DDG (German Digital Services Act)."}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16 lg:py-24 space-y-12">
        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "Diensteanbieter" : "Service provider"}
          </h2>
          <div className="text-slate-800 space-y-1 text-base">
            <div className="font-display text-xl font-bold text-slate-900">{COMPANY.name}</div>
            <div>{isDe ? `Inhaberin: ${COMPANY.owner}` : `Owner: ${COMPANY.owner}`}</div>
            <div>{COMPANY.street}</div>
            <div>{COMPANY.city}</div>
            <div>{COMPANY.country}</div>
          </div>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "Kontakt" : "Contact"}
          </h2>
          <ul className="space-y-2 text-base text-slate-800">
            <li>
              <span className="font-semibold w-24 inline-block">{isDe ? "Telefon:" : "Phone:"}</span>{" "}
              <a href="tel:+491708251779" className="text-slate-900 hover:text-cyan-600">
                {COMPANY.phone}
              </a>
            </li>
            <li>
              <span className="font-semibold w-24 inline-block">{isDe ? "E-Mail:" : "Email:"}</span>{" "}
              <a href={`mailto:${COMPANY.email}`} className="text-slate-900 hover:text-cyan-600 break-all">
                {COMPANY.email}
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "Umsatzsteuer" : "VAT"}
          </h2>
          <p className="text-base text-slate-700">
            {isDe
              ? "Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz: wird auf Anfrage mitgeteilt."
              : "VAT identification number pursuant to § 27a German VAT Act: provided on request."}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "Verantwortlich für den Inhalt" : "Responsible for the content"}
          </h2>
          <p className="text-base text-slate-700">
            {isDe ? "Verantwortlich gemäß § 18 Abs. 2 MStV: " : "Responsible according to § 18 (2) MStV: "}
            <span className="font-semibold">{COMPANY.owner}</span>, {COMPANY.street}, {COMPANY.city}.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "Streitschlichtung" : "Dispute resolution"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: "
              : "The European Commission provides a platform for online dispute resolution (ODR): "}
            <a
              href="https://ec.europa.eu/consumers/odr/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-600 hover:underline break-all"
            >
              https://ec.europa.eu/consumers/odr/
            </a>
            .
            <br />
            {isDe
              ? "Wir sind nicht bereit oder verpflichtet, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen."
              : "We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board."}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "Haftung für Inhalte" : "Liability for content"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen."
              : "As a service provider, we are responsible for our own content on these pages according to § 7 (1) DDG and general laws. According to §§ 8 to 10 DDG, however, we are not obliged to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity."}
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-bold text-cyan-600 uppercase tracking-[0.2em] text-[11px] mb-4">
            {isDe ? "Urheberrecht" : "Copyright"}
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            {isDe
              ? "Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung der jeweiligen Autorin."
              : "The content and works on these pages created by the site operators are subject to German copyright law. Duplication, processing, distribution and any kind of exploitation beyond the limits of copyright require the written consent of the respective author."}
          </p>
        </section>
      </main>
    </div>
  );
}
